<template>
	<div data-testid="onboarding-wizard" class="container px-4 py-6 mx-auto sm:px-6 sm:py-8">
		<div class="max-w-2xl mx-auto">
			<!-- Progress Bar -->
			<ProgressBar
				:current-step="storeCurrentStep"
				:total-steps="totalSteps"
				data-testid="onboarding-progress"
				class="mb-8"
			/>

			<!-- Step Content with Transitions -->
			<StepTransition :direction="transitionDirection">
				<!-- Dynamic Step Component or Summary -->
				<div
					v-if="storeCurrentStep === totalSteps"
					class="p-6 bg-white border border-gray-200 shadow-lg sm:p-8 rounded-2xl"
				>
					<!-- Header -->
					<div class="mb-6 text-center">
						<div
							class="inline-flex items-center justify-center w-12 h-12 mb-3 bg-blue-100 rounded-full"
						>
							<svg
								class="w-6 h-6 text-blue-600"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
							>
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
								/>
							</svg>
						</div>
						<h3 class="mb-1 text-xl font-bold text-gray-900 sm:text-2xl">Zusammenfassung</h3>
						<p class="text-sm text-gray-600">Überprüfen Sie Ihre Angaben bevor Sie fortfahren</p>
					</div>

					<!-- Summary Content -->
					<div class="space-y-4">
						<!-- Child Info Section -->
						<div class="p-4 bg-gray-50 border border-gray-200 rounded-lg">
							<h4 class="mb-3 text-xs font-semibold tracking-wide text-gray-500 uppercase">
								Informationen zum Kind
							</h4>
							<div class="space-y-2">
								<div class="flex items-baseline justify-between">
									<span class="text-sm text-gray-600">Name</span>
									<span class="font-medium text-gray-900">{{ formData.childName }}</span>
								</div>
								<div class="flex items-baseline justify-between">
									<span class="text-sm text-gray-600">Alter</span>
									<span class="font-medium text-gray-900">{{ formData.childAge }} Jahre</span>
								</div>
								<div class="flex items-baseline justify-between">
									<span class="text-sm text-gray-600">Schultyp</span>
									<span class="font-medium text-gray-900">{{ formData.childSchool }}</span>
								</div>
								<div class="pt-2 mt-2 border-t border-gray-200">
									<span class="text-xs text-gray-500">Lernsituation</span>
									<p class="mt-1 text-sm text-gray-900">{{ formData.childSituation }}</p>
								</div>
							</div>
						</div>

						<!-- Interests Section (if present) -->
						<div
							v-if="formData.childInterests"
							class="p-4 bg-gray-50 border border-gray-200 rounded-lg"
						>
							<h4 class="mb-2 text-xs font-semibold tracking-wide text-gray-500 uppercase">
								Interessen & Hobbys
							</h4>
							<p class="text-sm leading-relaxed text-gray-900">
								{{ formData.childInterests }}
							</p>
						</div>

						<!-- Challenges Section -->
						<div class="p-4 bg-gray-50 border border-gray-200 rounded-lg">
							<h4 class="mb-3 text-xs font-semibold tracking-wide text-gray-500 uppercase">
								Herausforderungen
							</h4>
							<div class="flex flex-wrap gap-2">
								<span
									v-for="challenge in selectedChallenges"
									:key="challenge.id"
									class="inline-flex items-center px-3 py-1.5 bg-white rounded-md text-sm text-gray-700 border border-gray-300"
								>
									{{ challenge.title }}
								</span>
							</div>
						</div>

						<!-- Feelings Section -->
						<div class="p-4 bg-gray-50 border border-gray-200 rounded-lg">
							<h4 class="mb-2 text-xs font-semibold tracking-wide text-gray-500 uppercase">
								Ihre Gefühle
							</h4>
							<div class="space-y-1">
								<p
									v-for="feeling in selectedFeelings"
									:key="feeling.id"
									class="text-sm leading-relaxed text-gray-900"
								>
									• {{ feeling.text }}
								</p>
							</div>
						</div>

						<!-- Goals Section -->
						<div class="p-4 bg-gray-50 border border-gray-200 rounded-lg">
							<h4 class="mb-3 text-xs font-semibold tracking-wide text-gray-500 uppercase">
								Ihre Ziele
							</h4>
							<div class="flex flex-wrap gap-2">
								<span
									v-for="goal in selectedGoals"
									:key="goal.id"
									class="inline-flex items-center px-3 py-1.5 bg-white rounded-md text-sm text-gray-700 border border-gray-300"
								>
									{{ goal.title }}
								</span>
							</div>
						</div>
					</div>
				</div>

				<!-- Dynamic Step Component for steps 1-4 -->
				<component
					:is="currentStepConfig?.component"
					v-else
					:key="`step-${storeCurrentStep}`"
					v-bind="getStepProps()"
					@update:model-value="handleModelValueUpdate"
					@validate="validateCurrentStep"
					@field-touched="markFieldAsTouched"
					@error="handleStepError"
				/>
			</StepTransition>

			<!-- Navigation Buttons -->
			<div class="flex justify-between mt-8">
				<!-- Left side: Back button or Home link -->
				<button
					v-if="storeCurrentStep > 1"
					class="px-6 py-3 font-medium text-gray-600 transition-colors duration-200 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
					data-testid="onboarding-back-btn"
					@click="onboardingStore.previousStep()"
				>
					← Zurück
				</button>
				<!-- Show "Zur Startseite" on first step -->
				<router-link
					v-else
					to="/"
					class="inline-flex items-center px-6 py-3 font-medium text-gray-600 transition-colors duration-200 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
					data-testid="onboarding-home-btn"
				>
					<svg
						class="w-5 h-5 mr-2"
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24"
						aria-hidden="true"
					>
						<path
							stroke-linecap="round"
							stroke-linejoin="round"
							stroke-width="2"
							d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
						/>
					</svg>
					Zur Startseite
				</router-link>

				<button
					v-if="storeCurrentStep < totalSteps"
					:disabled="!isStepValid"
					class="px-6 py-3 font-medium text-white transition-colors duration-200 bg-blue-600 rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
					data-testid="onboarding-next-btn"
					@click="handleNextStep"
				>
					Weiter →
				</button>

				<button
					v-else
					:disabled="!isStepValid"
					class="px-6 py-3 font-medium text-white transition-colors duration-200 bg-green-600 rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
					data-testid="onboarding-submit-btn"
					@click="submitForm"
				>
					Lerntypen Test starten
				</button>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue';
import { storeToRefs } from 'pinia';
import { useRouter } from 'vue-router';
import ProgressBar from '@/components/common/ProgressBar.vue';
import StepTransition from '@/components/transitions/StepTransition.vue';
import { useOnboardingStore } from '@/stores/onboarding';
import { useWizardStore } from '@/stores/wizard';
import { useConversionTracking, useFormTracking } from '@/composables/usePageTracking';

// Import step configuration and components
import {
	stepsConfig,
	getTotalSteps,
	challengeOptions,
	feelingOptions,
	goalOptions,
} from './config/steps';

// Emits
const emit = defineEmits<{
	complete: [data: any];
}>();

// Store and Router
const onboardingStore = useOnboardingStore();
const router = useRouter();
const wizardStore = useWizardStore();

// Initialize tracking
const { trackFunnelStep } = useConversionTracking();
useFormTracking('onboarding');

// Access store properties using storeToRefs for proper reactivity
const { formData, currentStep: storeCurrentStep, skipSummary } = storeToRefs(onboardingStore);

// Tracking state
const onboardingStartTime = ref<number>(Date.now());

// Computed properties
const selectedChallenges = computed(() => {
	const challenges = formData.value.challenges || [];
	return challengeOptions.filter((challenge) => challenges.includes(challenge.id));
});

const selectedFeelings = computed(() => {
	const feelings = formData.value.parentFeelings || [];
	return feelingOptions.filter((feeling) => feelings.includes(feeling.id));
});

const selectedGoals = computed(() => {
	const goals = formData.value.selectedGoals || [];
	return goalOptions.filter((goal) => goals.includes(goal.id));
});

// Step navigation using store
const totalSteps = getTotalSteps();
const transitionDirection = ref<'slide-right' | 'slide-left'>('slide-right');

// Get current step configuration
const currentStepConfig = computed(() => {
	return stepsConfig.find((step) => step.id === storeCurrentStep.value);
});

const isStepValid = computed(() => onboardingStore.validateCurrentStep());

// Computed properties were moved to avoid duplication

// Methods
function validateCurrentStep(): boolean {
	return onboardingStore.validateCurrentStep();
}

function markFieldAsTouched(fieldName: string): void {
	// Use the store's method to mark field as touched
	if (!onboardingStore.touchedFields.includes(fieldName)) {
		onboardingStore.touchedFields.push(fieldName);
	}
}

// Get props for the current step component
function getStepProps() {
	const step = stepsConfig.find((s) => s.id === storeCurrentStep.value);

	// Create errors object from store errors for current step
	const stepErrors: Record<string, string> = {};
	onboardingStore.errors.forEach((error) => {
		stepErrors[error.field] = error.message;
	});

	const baseProps = {
		errors: stepErrors,
		touchedFields: onboardingStore.touchedFields,
		...step?.props,
	};

	// For step 1, add the formData as v-model value
	if (storeCurrentStep.value === 1) {
		return {
			...baseProps,
			modelValue: formData.value,
		};
	}

	// For step 2, add challenges as v-model value
	if (storeCurrentStep.value === 2) {
		return {
			...baseProps,
			modelValue: formData.value.challenges,
		};
	}

	// For step 3, add parentFeelings as v-model value
	if (storeCurrentStep.value === 3) {
		return {
			...baseProps,
			modelValue: formData.value.parentFeelings,
		};
	}

	// For step 4, add selectedGoals as v-model value
	if (storeCurrentStep.value === 4) {
		return {
			...baseProps,
			modelValue: formData.value.selectedGoals,
		};
	}

	return baseProps;
}

// Handle model value updates from step components
function handleModelValueUpdate(value: any): void {
	// Update the store based on current step
	if (storeCurrentStep.value === 1) {
		// Step 1: Update entire form data object
		Object.assign(formData.value, value);
	} else if (storeCurrentStep.value === 2) {
		// Step 2: Update challenges array
		formData.value.challenges = value;
	} else if (storeCurrentStep.value === 3) {
		// Step 3: Update parent feelings
		formData.value.parentFeelings = value;
	} else if (storeCurrentStep.value === 4) {
		// Step 4: Update selected goals
		formData.value.selectedGoals = value;
	}

	// Trigger validation after update
	validateCurrentStep();
}

// Handle step error from child components
function handleStepError(error: string): void {
	if (error && error.trim() !== '') {
		console.error('Step error:', error);
	}
}

// Handle next step logic (including skipSummary check)
function handleNextStep(): void {
	// If skipSummary is enabled and we are at step 4 (Goals), submit immediately
	// Use direct access to .value for refs from storeToRefs
	if (skipSummary.value && storeCurrentStep.value === 4) {
		// Mark fields as touched (important for validation before submit)
		onboardingStore.markCurrentStepFieldsAsTouched();
		// Validate current step before proceeding
		if (onboardingStore.validateCurrentStep()) {
			submitForm();
		}
	} else {
		// Otherwise proceed normally
		onboardingStore.nextStep();
	}
}

// Submit the form
function submitForm(): void {
	if (validateCurrentStep()) {
		try {
			const completedData = onboardingStore.complete();

			// Track onboarding completion
			// @ts-ignore - ignoring type issue for tracking utility
			trackFunnelStep('onboarding_completed', {
				totalTime: Date.now() - onboardingStartTime.value,
				childAge: completedData.childAge,
				challenges: completedData.challenges,
				goals: completedData.selectedGoals,
				parentFeelings: completedData.parentFeelings,
			});

			// Store onboarding data in wizard store for context
			// ✅ UPDATED: Now transfers all fields including childGender and childInterests
			wizardStore.setBasicInfo({
				childName: completedData.childName || '',
				childAge: completedData.childAge || 0,
				childSchool: completedData.childSchool || '',
				childSituation: completedData.childSituation || '',
				childInterests: completedData.childInterests || '',
				childGender: completedData.childGender || 'other',
			});

			// Store onboarding context separately
			wizardStore.setOnboardingContext({
				challenges: completedData.challenges,
				parentFeelings: completedData.parentFeelings,
				selectedGoals: completedData.selectedGoals,
				startedAt: completedData.startedAt,
				completedAt: completedData.completedAt,
			});

			// Route to Phase 1 of the wizard
			router.push({ name: 'wizard-phase1', params: { step: '1' } });

			emit('complete', completedData);
		} catch (error) {
			console.error('Failed to complete onboarding:', error);
		}
	}
}

// Watch for step changes to update transition direction and scroll to top
watch(
	() => storeCurrentStep.value,
	(newStep, oldStep) => {
		if (newStep > oldStep) {
			transitionDirection.value = 'slide-right';
		} else {
			transitionDirection.value = 'slide-left';
		}

		// Scroll to top on step change for better UX
		window.scrollTo({ top: 0, behavior: 'smooth' });
	},
);

// Watch for changes in form data to trigger validation
watch(
	() => formData.value,
	() => {
		onboardingStore.validateCurrentStep();
	},
	{ deep: true },
);

// Initialize tracking on mount
onMounted(() => {
	onboardingStartTime.value = Date.now();
	trackFunnelStep('onboarding_started');
});
</script>